/**
 * "Who else should see this?" for the rows a person AUTHORS.
 *
 * A tracer, an alert rule, a grouping rule, a pipeline definition, a saved view.
 * They are not overlays — nothing is being laid over a subject — but they ask
 * the operator the same question at creation time, and they used to each answer
 * it their own way: a hand-picked team list in every dialog, a hard-coded rung
 * per surface, and baselines that quietly disagreed (a new grouping rule was
 * tenant-wide, a new tracer owner-only). None of them consulted the caller's
 * configured Settings › Default visibility.
 *
 * This is the one seam that closes that. It reads the SAME resolved default the
 * overlay writes use — user override, then the workspace/plan layers, then the
 * product baseline, all validated against the resource's offered rungs — and
 * translates it into the shape these rows store through the shared
 * {@link recordVisibilityReport} mapper.
 *
 * Deliberately a hook over the two things a surface would otherwise reach for
 * separately: the resolved tier and the caller's teams. A `team` default with
 * the teams fetched somewhere else is how a share ends up naming nobody, and
 * the mapper can only degrade that to `private` if it is handed the ids in the
 * first place.
 *
 * And the degradation is now SAID. A `team` default with an empty team list
 * writes a private row, which is correct — but the list is empty both when you
 * are on no team and when the read failed, and in the second case the operator
 * believes they just shared something with their colleagues. That is the one
 * bug in this file worth naming: silence at the point of writing.
 *
 * Said from an EFFECT, not from the callback. Two callers compute a dialog's
 * default scope in their JSX, so notifying inside the callback was a side effect
 * in the render phase — ref-guarded against looping, but still a write to the
 * outside world from a render.
 */
import * as React from "react";
import { notifyWarning } from "@authe/ui-core";
import {
  recordVisibilityReport,
  type AttributionResource,
  type RecordVisibility,
  type RecordVisibilityReport,
} from "@authe/fe-commons/attribution";
import { useScopePrefs } from "./scopePrefs";
import { useMyTeamsRead } from "./useMyTeams";

export type { RecordVisibility, RecordVisibilityReport };

/** Copy for a downgrade, split by WHY the team list is empty. */
function downgradeNotice(teamsFailed: boolean): { title: string; detail: string } {
  return teamsFailed
    ? {
        title: "Saved private — your team list didn’t load",
        detail:
          "Your default is to share with your teams, but that list couldn’t be read, so this was created private. Reload and edit its visibility to share it.",
      }
    : {
        title: "Saved private — you’re not on a team yet",
        detail:
          "Your default is to share with your teams, and you don’t belong to one, so this was created private.",
      };
}

/**
 * The visibility a NEW record of `resource` should be created with.
 *
 * Returns both halves because the two travel together on every one of these
 * routes: `scope` is the rung the row is written at, `visibleToTeamIds` the
 * ABAC carrier read over that partition. A resource whose wire carries no rung
 * (an alert rule) uses the carrier alone — and its policy offers only the tiers
 * that survive that, so the discarded case cannot arise.
 *
 * The returned object also carries `requested` / `effective` / `downgraded`
 * ({@link RecordVisibilityReport}), and calling it WARNS when the tier could
 * not be honoured. The warning fires here, at the write, rather than only in
 * the settings panel's `noTeams` hint — the person creating a tracer is not
 * looking at Settings, and the row they are about to create is the thing that
 * ends up private.
 */
export function useRecordVisibility(): (resource: AttributionResource) => RecordVisibilityReport {
  const { resolveDefaultWriteScope } = useScopePrefs();
  const { teams, failed } = useMyTeamsRead();
  const teamIds = React.useMemo(() => teams.map((team) => team.id), [teams]);
  // One warning per distinct downgrade, not one per keystroke: these dialogs
  // re-seed their blank form whenever the resolved default changes.
  const warned = React.useRef(new Set<string>());
  // Notices QUEUED by the callback, toasted outside the render phase.
  //
  // Two of this hook's callers compute a dialog's default scope in their JSX —
  // `ExplorerSavedViewsBar` and `GraphSavedViewsBar` both call it while
  // rendering — so firing the toast from inside the callback made it a
  // render-phase side effect. The `warned` ref stopped it looping, which is why
  // it was survivable; it was still a write to the outside world from a render.
  const pending = React.useRef<{ title: string; detail: string }[]>([]);

  const flush = React.useCallback((): void => {
    const queued = pending.current;
    if (queued.length === 0) return;
    // Drained before notifying, so whichever flush runs first does the work and
    // the other finds nothing.
    pending.current = [];
    for (const notice of queued) notifyWarning(notice.title, notice.detail);
  }, []);

  // No dependency array: this runs after EVERY render, which is exactly when a
  // queue put there during one can exist. It early-returns when there is none.
  React.useEffect(() => {
    flush();
  });

  return React.useCallback(
    (resource: AttributionResource) => {
      const report = recordVisibilityReport({
        scope: resolveDefaultWriteScope(resource),
        teamIds,
      });
      if (report.downgraded) {
        const key = `${resource}:${report.downgraded.from}->${report.downgraded.to}:${failed ? "failed" : "none"}`;
        if (!warned.current.has(key)) {
          warned.current.add(key);
          pending.current.push(downgradeNotice(failed));
          // The other callers ask from an event handler (seeding a blank form,
          // saving a pipeline), where a re-render is likely but not promised.
          // A macrotask is outside any render phase and is a no-op whenever the
          // effect above got there first.
          setTimeout(flush, 0);
        }
      }
      return report;
    },
    [failed, flush, resolveDefaultWriteScope, teamIds],
  );
}
